import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import type { SmartAdText } from "../state/formReducer"

/**
 * Smart Creative ad-text editor. 1-5 entries, each with a live
 * character counter. Parity: `creatives.js:120-182`.
 *
 *   - Empty-state dispatches to reducer to auto-seed the first row
 *     (handled there).
 *   - `+ Add` button hidden at 5 rows (reducer blocks ADD_SMART_AD_TEXT
 *     past 5 anyway — double defence).
 *   - Delete button blocked below 1 row, mirroring legacy alert copy.
 *   - Duplicate titles are highlighted; TikTok rejects them on Smart+.
 */

interface Props {
  texts: SmartAdText[]
  onUpdate: (id: number, text: string) => void
  onAdd: () => void
  onRemove: (id: number) => void
}

function duplicateIds(texts: SmartAdText[]): Set<number> {
  const counts = new Map<string, number>()
  for (const t of texts) {
    const key = t.text.trim()
    if (!key) continue
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  const dupes = new Set<number>()
  for (const t of texts) {
    const key = t.text.trim()
    if (key && (counts.get(key) ?? 0) > 1) dupes.add(t.id)
  }
  return dupes
}

export function AdTextsSmart({ texts, onUpdate, onAdd, onRemove }: Props) {
  const dupes = duplicateIds(texts)

  return (
    <div>
      <div className="flex items-center gap-2 text-sm">
        <span className="font-semibold">Text</span>
        <span
          className="text-xs text-white"
          title="Add multiple ad text variations for Smart Creative"
        >
          ⓘ
        </span>
      </div>
      <div className="mt-2 space-y-2">
        {texts.map((t) => {
          const isDupe = dupes.has(t.id)
          return (
            <div
              key={t.id}
              className={
                isDupe
                  ? "rounded-md border border-red-500/70 bg-brand-bg/40 p-2"
                  : "rounded-md border border-brand-border bg-brand-bg/40 p-2"
              }
            >
              <Textarea
                value={t.text}
                maxLength={100}
                rows={2}
                required
                placeholder="Enter text for your ad"
                onChange={(e) => onUpdate(t.id, e.target.value)}
                aria-invalid={isDupe}
              />
              <div className="mt-1 flex items-center justify-between text-xs">
                <span className={isDupe ? "text-red-400" : "text-white"}>
                  {isDupe
                    ? "Duplicate — TikTok requires unique texts"
                    : `${t.text.length}/100`}
                </span>
                <button
                  type="button"
                  className="text-red-400 hover:text-red-300 disabled:opacity-40"
                  disabled={texts.length <= 1}
                  onClick={() => {
                    if (texts.length <= 1) {
                      window.alert(
                        "You need at least 1 ad text for Smart Creative.",
                      )
                      return
                    }
                    onRemove(t.id)
                  }}
                  aria-label="Remove ad text"
                >
                  🗑️
                </button>
              </div>
            </div>
          )
        })}
      </div>
      {texts.length < 5 ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={onAdd}
        >
          + Add
        </Button>
      ) : null}
      <p className="mt-1 text-xs text-white">
        Add 1-5 unique ad text variations. TikTok will optimize which text
        performs best.
      </p>
    </div>
  )
}
